import { useCallback, useRef } from "react";
import { useQueryClient } from "react-query";
import useGlobalContext from "hooks/globalData";
import { SwitchOrganizationRequest } from "services/KnowfullyAdminClient";
import { saveAuthState } from "stores/authStore";
import { ClaimType, UserClaim } from "types/claimTypes";

const useOrganizationSwitch = () => {
  const { organizationService, authenticationService } = useGlobalContext();
  const queryClient = useQueryClient();
  const isSwitching = useRef(false);

  const switchOrganization = useCallback(async (organizationId: string) => {
    if (isSwitching.current) {
      return;
    }

    isSwitching.current = true;

    try {
      const switchResponse = await organizationService.switchOrganization(
        new SwitchOrganizationRequest({ organizationId })
      );

      if (!switchResponse.data?.sessionId) {
        queryClient.invalidateQueries();
        return;
      }

      const loginResponse = await authenticationService.loginComplete(
        switchResponse.data.sessionId
      );

      if (!loginResponse.data?.result) {
        return;
      }

      const authResponse = loginResponse.data;
      const claims: (UserClaim | undefined)[] =
        authResponse.permissions?.map((perm: string) => {
          const splitted = perm.split(".");
          return {
            claimType: ClaimType.Permission,
            parentClaim: splitted[0],
            claimResource: splitted[1],
            claimPermission: splitted[2],
          } as UserClaim;
        }) ?? [];

      saveAuthState({
        id: authResponse.id ?? null,
        email: authResponse.email,
        firstName: authResponse.firstName,
        lastName: authResponse.lastName,
        isLoggedIn: true,
        claims,
      });

      queryClient.invalidateQueries();
    } finally {
      isSwitching.current = false;
    }
  }, [organizationService, authenticationService, queryClient]);

  return { switchOrganization };
};

export default useOrganizationSwitch;
